"use client";

import WalkthroughVideo from "@/components/guides/WalkthroughVideo";
import { helpSectionAnchor } from "@/lib/guides/help-sections";
import { getWalkthroughVideo } from "@/lib/guides/walkthrough-videos";
import { renderSanitizedMarkdown } from "@/lib/markdown";

/**
 * One help article, from a `content.json` beside its page:
 *
 *   { title, description, sections: [{ id?, title, video?, content }] }
 *
 * `content` is markdown. `video` names a row in lib/guides/walkthrough-videos.js
 * and plays above the text. `id` is the anchor (`helpSectionAnchor`); when
 * absent it is derived from the title, so a section a component links to by id
 * (the simulator's "How this works" dialog) should set one and keep it stable.
 */

/** A section's video and prose, without the heading. Shared with dialogs. */
export function HelpSectionBody({ section }) {
  const video = section.video ? getWalkthroughVideo(section.video) : null;
  return (
    <div className="space-y-4">
      {video && (
        <WalkthroughVideo
          src={video.src}
          poster={video.poster}
          title={video.title}
        />
      )}
      <div
        className="max-w-none prose prose-slate"
        dangerouslySetInnerHTML={{
          __html: renderSanitizedMarkdown(section.content),
        }}
      />
    </div>
  );
}

export default function HelpContent({ content }) {
  return (
    <div className="max-w-4xl">
      <h1 className="mb-4 text-4xl font-bold">{content.title}</h1>
      <p className="mb-8 text-xl text-gray-600">{content.description}</p>

      {/* Table of Contents */}
      <nav className="p-6 mb-8 bg-action-50 border border-action-200 rounded-lg">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">On This Page</h2>
        <ul className="space-y-2">
          {content.sections.map((section) => {
            const anchorId = helpSectionAnchor(section);
            return (
              <li key={anchorId}>
                <a
                  href={`#${anchorId}`}
                  className="text-action-600 hover:text-action-800 hover:underline"
                >
                  {section.title}
                </a>
              </li>
            );
          })}
        </ul>
      </nav>

      <div className="space-y-12">
        {content.sections.map((section) => {
          const anchorId = helpSectionAnchor(section);
          return (
            <section
              key={anchorId}
              id={anchorId}
              className="p-6 bg-white rounded-lg shadow-xs scroll-mt-8"
            >
              <h2 className="mb-4 text-2xl font-semibold">
                <a href={`#${anchorId}`} className="hover:text-action-600 transition-colors">
                  {section.title}
                </a>
              </h2>
              <HelpSectionBody section={section} />
            </section>
          );
        })}
      </div>
    </div>
  );
}
